/**
 * The Super Administration Zone.
 *
 * Owner decision, 2026-08-08: two named addresses sign in with an email, a PIN and a password —
 * no magic link — and get everything. The session never expires; only Logout ends it.
 *
 * FIVE SECTIONS, matching what it takes to actually serve a customer:
 *
 *  · Customers   — every dog, opened as the customer's own portal (the staff console does this
 *                  already, so this reuses it rather than growing a second one), plus the
 *                  emergency contact the staff console deliberately withholds.
 *  · Operations  — what the system is doing: sync health, counts, sessions, mail transport.
 *  · Meal links  — turn machine matches into named, attributed human decisions.
 *  · Access      — who can get in: the pilot allowlist and the staff accounts.
 *  · Audit       — who did what. The reason a console beats a shared psql prompt.
 *
 * It is read-mostly by construction. It can admit a household, manage staff and trigger a sync; it
 * cannot edit a customer's record, because nothing in this portal can — v1 is read-only against
 * JotForm through the validated meal-edit queue and shared write-back worker.
 */

import { useCallback, useEffect, useRef, useState, type ReactElement, type ReactNode } from 'react'
import {
  CANNOT_REMOVE_SELECTION_MESSAGE,
  MEAL_FORM,
  fmtDay,
  parseDay,
  type AdminAllowlistEntry,
  type AdminAuditEntry,
  type AdminConfirmedMealLink,
  type AdminExactMealLink,
  type AdminMealLinkQueue,
  type AdminMealLinkRow,
  type AdminOperations,
  type AdminPendingMealLink,
  type AdminStaffMember,
  type StaffDogSummary,
  type StaffSessionInfo,
} from '@ft/shared'

import {
  adminAllowlist,
  adminAudit,
  adminConfirmMealLink,
  adminMealLinks,
  adminMealLinkEdit,
  adminNoMatchMealLink,
  adminOperations,
  adminStaff,
  adminSync,
  adminWrite,
  staffDogs,
} from './api'
import { MealFieldEditor } from '../components/MealFieldEditor'

type Tab = 'customers' | 'operations' | 'meal-links' | 'access' | 'audit'

const TABS: readonly { readonly id: Tab; readonly label: string }[] = [
  { id: 'customers', label: 'Customers' },
  { id: 'operations', label: 'Operations' },
  { id: 'meal-links', label: 'Meal links' },
  { id: 'access', label: 'Access' },
  { id: 'audit', label: 'Audit' },
]

export function AdminZone({
  who,
  onSignOut,
  customers,
}: {
  readonly who: StaffSessionInfo
  readonly onSignOut: () => void
  /** The dog list and record viewer, reused from the staff console rather than rebuilt. */
  readonly customers: ReactElement
}): ReactElement {
  const [tab, setTab] = useState<Tab>('customers')

  return (
    <div style={{ minHeight: '100vh' }}>
      <header
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 30,
          padding: '10px 18px',
          background: 'var(--color-accent-800)',
          color: 'var(--color-bg)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <strong style={{ fontSize: '14px' }}>Fairy Tails · Super Administration</strong>
          <span style={{ fontSize: '12.5px', opacity: 0.85 }}>{who.email}</span>
          <button
            type="button"
            onClick={onSignOut}
            style={{
              marginLeft: 'auto',
              border: 0,
              background: 'none',
              font: 'inherit',
              color: 'inherit',
              textDecoration: 'underline',
              cursor: 'pointer',
              padding: '6px 0',
              fontSize: '13px',
            }}
          >
            Logout
          </button>
        </div>
        <nav style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '8px' }}>
          {TABS.map((entry) => (
            <button
              key={entry.id}
              type="button"
              onClick={() => setTab(entry.id)}
              aria-current={tab === entry.id ? 'page' : 'false'}
              style={{
                border: 0,
                cursor: 'pointer',
                font: 'inherit',
                fontSize: '13px',
                fontWeight: 700,
                padding: '7px 14px',
                borderRadius: '999px',
                background: tab === entry.id ? 'var(--color-bg)' : 'transparent',
                color: tab === entry.id ? 'var(--color-accent-800)' : 'var(--color-bg)',
              }}
            >
              {entry.label}
            </button>
          ))}
        </nav>
      </header>

      {/* Customers keeps its own full-bleed layout; the rest sit in a column. */}
      {tab === 'customers' ? (
        customers
      ) : (
        <main style={{ maxWidth: '980px', margin: '0 auto', padding: '20px 18px 60px' }}>
          {tab === 'operations' ? <Operations /> : null}
          {tab === 'meal-links' ? <MealLinks /> : null}
          {tab === 'access' ? <Access /> : null}
          {tab === 'audit' ? <Audit /> : null}
        </main>
      )}
    </div>
  )
}

function Card({
  title,
  children,
}: {
  readonly title: string
  // `ReactNode`, not `ReactElement`: several of these cards render `{x === null ? null : <p/>}`,
  // and a null branch is not assignable to ReactElement.
  readonly children: ReactNode
}): ReactElement {
  return (
    <section className="card" style={{ marginBottom: '14px', padding: '18px', gap: 0 }}>
      <h2 style={{ margin: '0 0 12px', fontSize: '16px' }}>{title}</h2>
      {children}
    </section>
  )
}

function Stat({ label, value }: { readonly label: string; readonly value: string | number }): ReactElement {
  return (
    <div>
      <div className="text-muted" style={{ fontSize: '12px' }}>
        {label}
      </div>
      <div style={{ fontSize: '22px', fontWeight: 700, lineHeight: 1.2 }}>{value}</div>
    </div>
  )
}

function Operations(): ReactElement {
  const [data, setData] = useState<AdminOperations | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [syncing, setSyncing] = useState(false)
  const [note, setNote] = useState<string | null>(null)

  const load = useCallback(() => {
    void adminOperations().then((result) => {
      if (result.ok) setData(result.data)
      else setError(result.message)
    })
  }, [])
  useEffect(load, [load])

  if (error !== null) return <p role="alert" className="card" style={{ padding: '16px' }}>{error}</p>
  if (data === null) return <p className="text-muted">Loading…</p>

  const staleness =
    data.sync.lastSuccessAt === null
      ? 'never'
      : `${Math.round((Date.now() - new Date(data.sync.lastSuccessAt).getTime()) / 60_000)} min ago`

  return (
    <>
      <Card title="The record cache">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '16px' }}>
          <Stat label="Households" value={data.households} />
          <Stat label="Dogs" value={data.dogs} />
          <Stat label="Contact rows" value={data.contacts} />
          <Stat label="Retired" value={data.retiredHouseholds} />
        </div>
      </Card>

      <Card title="Feeding plans">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '16px' }}>
          <Stat label="Meal rows" value={data.mealRows} />
          <Stat label="Human-confirmed" value={data.mealLinksConfirmed} />
          <Stat label="Proposed" value={data.mealLinksProposed} />
          <Stat label="Unmatched" value={data.mealLinksUnmatched} />
        </div>
        <p className="text-muted" style={{ margin: '12px 0 0', fontSize: '12.5px', lineHeight: 1.6 }}>
          Only a human-confirmed link is ever shown to a client. Dog name plus surname resolves
          about 62% of meal rows, so the rest wait for somebody to decide rather than risk showing
          one dog’s feeding plan to another’s owner.
        </p>
      </Card>

      <Card title="Sync">
        <dl style={{ margin: 0, display: 'grid', gridTemplateColumns: 'minmax(150px,auto) 1fr', gap: '5px 16px', fontSize: '13.5px' }}>
          {[
            ['Last success', staleness],
            ['Last run', data.sync.lastRunAt === null ? '—' : new Date(data.sync.lastRunAt).toLocaleString('en-GB')],
            ['Rows last run', data.sync.rowsLastRun ?? '—'],
            ['Consecutive failures', data.sync.consecutiveFailures],
            ['Last error', data.sync.lastError ?? 'none'],
            ['Enabled', data.sync.enabled ? 'yes' : 'no'],
            ['Every', `${data.sync.intervalMinutes} minutes`],
          ].map(([label, value]) => (
            <div key={String(label)} style={{ display: 'contents' }}>
              <dt className="text-muted">{label}</dt>
              <dd style={{ margin: 0, overflowWrap: 'anywhere' }}>{String(value)}</dd>
            </div>
          ))}
        </dl>
        <button
          type="button"
          className="btn btn-secondary"
          disabled={syncing}
          onClick={() => {
            setSyncing(true)
            setNote(null)
            void adminSync().then((result) => {
              setSyncing(false)
              setNote(
                result.ok
                  ? `Sync ${result.data.ok ? 'succeeded' : 'FAILED'} — ${result.data.households} households, ${result.data.dogs} dogs${result.data.error ? ` · ${result.data.error}` : ''}`
                  : result.message
              )
              load()
            })
          }}
          style={{ minHeight: '42px', marginTop: '14px' }}
        >
          {syncing ? 'Syncing…' : 'Run a sync now'}
        </button>
        {note === null ? null : (
          <p role="status" style={{ margin: '10px 0 0', fontSize: '13px' }}>
            {note}
          </p>
        )}
      </Card>

      <Card title="Access and delivery">
        <dl style={{ margin: 0, display: 'grid', gridTemplateColumns: 'minmax(150px,auto) 1fr', gap: '5px 16px', fontSize: '13.5px' }}>
          {[
            ['Pilot mode', data.pilotMode ? 'ON — only allowlisted households can sign in' : 'off — any known household can sign in'],
            ['Households admitted', data.allowlisted],
            ['Mail transport', data.mailer === 'resend' ? 'Resend (sending for real)' : 'console (links printed to the log, NOT emailed)'],
            ['Live customer sessions', data.sessions.clients],
            ['Live staff sessions', data.sessions.staff],
          ].map(([label, value]) => (
            <div key={String(label)} style={{ display: 'contents' }}>
              <dt className="text-muted">{label}</dt>
              <dd style={{ margin: 0 }}>{String(value)}</dd>
            </div>
          ))}
        </dl>
      </Card>
    </>
  )
}

function mealRowName(row: AdminMealLinkRow): string {
  return row.dogName ?? row.mealSubmissionId
}

function householdLabel(ownerName: string | null, ownerEmail: string | null, fallback: string): string {
  const household = ownerName ?? fallback
  return ownerEmail === null ? household : `${household}, ${ownerEmail}`
}

function submittedLabel(value: string | null): string {
  return value === null ? 'Submission date unavailable' : new Date(value).toLocaleString('en-GB')
}

function SearchAttach({
  row,
  disabled,
  onAttach,
}: {
  readonly row: AdminMealLinkRow
  readonly disabled: boolean
  readonly onAttach: (dog: StaffDogSummary) => void
}): ReactElement {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<readonly StaffDogSummary[]>([])
  const [loading, setLoading] = useState(false)
  const [searched, setSearched] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const search = (): void => {
    if (query.trim() === '') {
      setError('Enter a dog, owner or email to search.')
      return
    }
    setLoading(true)
    setSearched(true)
    setError(null)
    void staffDogs(query).then((result) => {
      setLoading(false)
      if (result.ok) setResults(result.data.dogs)
      else setError(result.message)
    })
  }

  return (
    <div style={{ marginTop: '10px' }}>
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
        <input
          className="input"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Dog, owner or email"
          aria-label={`Find a dog for ${mealRowName(row)}`}
          style={{ minHeight: '40px', flex: '1 1 220px' }}
        />
        <button type="button" className="btn btn-secondary" disabled={disabled || loading} onClick={search}>
          {loading ? 'Searching…' : 'Find dog'}
        </button>
      </div>
      {error === null ? null : <p role="alert" style={{ margin: '8px 0 0' }}>{error}</p>}
      {searched && !loading && error === null && results.length === 0 ? (
        <p className="text-muted" style={{ margin: '8px 0 0' }}>No active dogs found.</p>
      ) : null}
      {results.length === 0 ? null : (
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '8px' }}>
          {results.map((dog) => (
            <button
              key={dog.dogId}
              type="button"
              className="btn btn-ghost"
              disabled={disabled}
              onClick={() => onAttach(dog)}
            >
              Attach {dog.dogName} — {householdLabel(dog.ownerName, dog.email, 'Household unknown')}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

function MealRowHeader({ row, tag }: { readonly row: AdminMealLinkRow; readonly tag: string }): ReactElement {
  return (
    <div style={{ display: 'flex', alignItems: 'start', gap: '10px', flexWrap: 'wrap' }}>
      <div style={{ flex: '1 1 220px' }}>
        <strong>{mealRowName(row)}</strong>
        <span className="text-muted"> · {row.parentSurname ?? 'surname unavailable'}</span>
        <div className="text-muted" style={{ fontSize: '12px', marginTop: '3px' }}>
          {submittedLabel(row.submittedAt)} · {row.mealSubmissionId}
        </div>
      </div>
      <span className={`tag ${tag === 'Needs decision' ? 'tag-accent' : 'tag-accent-2'}`}>{tag}</span>
    </div>
  )
}

function MealEditPanel({
  row,
  onSaved,
}: {
  readonly row: AdminConfirmedMealLink
  readonly onSaved: () => Promise<void>
}): ReactElement {
  const cloneValues = (): Record<string, string | string[]> =>
    Object.fromEntries(
      Object.entries(row.editValues).map(([qid, value]) => [qid, typeof value === 'string' ? value : [...value]])
    )
  const [values, setValues] = useState<Record<string, string | string[]>>(cloneValues)
  const [message, setMessage] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const savingRef = useRef(false)

  const setValue = (qid: string, value: string | string[]): void => {
    const original = row.editValues[qid]
    if (
      Array.isArray(value) &&
      Array.isArray(original) &&
      original.some((selection) => !value.includes(selection))
    ) {
      setMessage(CANNOT_REMOVE_SELECTION_MESSAGE)
      return
    }
    setValues((current) => ({ ...current, [qid]: value }))
    setMessage(null)
  }

  const save = async (): Promise<void> => {
    if (savingRef.current) return
    const changes: Record<string, string | string[]> = {}
    for (const field of MEAL_FORM.fields.filter((candidate) => !candidate.hidden)) {
      const before = row.editValues[field.qid]
      const after = values[field.qid]
      if (JSON.stringify(before) !== JSON.stringify(after) && after !== undefined) changes[field.qid] = after
    }
    if (Object.keys(changes).length === 0) return
    if (Object.values(changes).some((value) => value === '' || (Array.isArray(value) && value.length === 0))) {
      setMessage('JotForm cannot clear or remove a recorded value; change it on the JotForm form itself for now.')
      return
    }

    savingRef.current = true
    setSaving(true)
    setMessage(null)
    const result = await adminMealLinkEdit({ mealSubmissionId: row.mealSubmissionId, changes })
    if (!result.ok) {
      savingRef.current = false
      setSaving(false)
      setMessage(result.message)
      return
    }
    await onSaved()
  }

  return (
    <div className="card" style={{ marginTop: '12px', padding: '14px', display: 'grid', gap: '14px' }}>
      <MealFieldEditor
        fields={MEAL_FORM.fields.filter((field) => !field.hidden)}
        values={values}
        onChange={setValue}
      />
      {message === null ? null : <p role="alert" style={{ margin: 0 }}>{message}</p>}
      <div>
        <button type="button" className="btn btn-primary" disabled={saving} onClick={() => void save()}>
          {saving ? 'Saving…' : 'Save changes'}
        </button>
      </div>
    </div>
  )
}

function MealLinks(): ReactElement {
  const [data, setData] = useState<AdminMealLinkQueue | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [note, setNote] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [editing, setEditing] = useState<string | null>(null)

  const load = useCallback(async (): Promise<void> => {
    const result = await adminMealLinks()
    if (result.ok) {
      setData(result.data)
      setError(null)
    } else {
      setError(result.message)
    }
  }, [])
  useEffect(() => {
    void load()
  }, [load])

  const confirm = async (row: AdminMealLinkRow, dogId: string | null): Promise<void> => {
    setBusy(row.mealSubmissionId)
    setError(null)
    setNote(null)
    const result =
      dogId === null
        ? await adminNoMatchMealLink({ mealSubmissionId: row.mealSubmissionId })
        : await adminConfirmMealLink({ mealSubmissionId: row.mealSubmissionId, dogId })
    setBusy(null)
    if (!result.ok) {
      setError(`${mealRowName(row)}: ${result.message}`)
      return
    }
    setNote(`${mealRowName(row)} has been decided.`)
    await load()
  }

  const confirmAllExact = async (): Promise<void> => {
    if (data === null) return
    setBusy('confirm-all')
    setError(null)
    setNote(`Confirming 0 of ${data.exact.length} exact matches…`)
    for (let index = 0; index < data.exact.length; index += 1) {
      const row = data.exact[index]
      if (!row) continue
      setNote(`Confirming ${index + 1} of ${data.exact.length}: ${mealRowName(row)}…`)
      const result = await adminConfirmMealLink({
        mealSubmissionId: row.mealSubmissionId,
        dogId: row.dogId,
      })
      if (!result.ok) {
        setBusy(null)
        setError(`Stopped at ${mealRowName(row)} (${row.mealSubmissionId}): ${result.message}`)
        setNote(`Confirmed ${index} of ${data.exact.length} exact matches.`)
        return
      }
    }
    setBusy(null)
    setNote(`Confirmed all ${data.exact.length} exact matches.`)
    await load()
  }

  if (data === null && error !== null) {
    return <p role="alert" className="card" style={{ padding: '16px' }}>{error}</p>
  }
  if (data === null) return <p className="text-muted">Loading meal-link decisions…</p>

  const total = data.pending.length + data.unmatched.length + data.exact.length
  const confirmed = data.confirmed ?? []
  const candidateButton = (row: AdminPendingMealLink, candidate: AdminPendingMealLink['candidates'][number]) => (
    <button
      key={candidate.dogId}
      type="button"
      className="btn btn-primary"
      disabled={busy !== null}
      onClick={() => void confirm(row, candidate.dogId)}
    >
      Confirm {candidate.dogName} — {householdLabel(candidate.ownerName, candidate.ownerEmail, candidate.surname)}
    </button>
  )

  const rowActions = (row: AdminMealLinkRow) => (
    <>
      <button
        type="button"
        className="btn btn-ghost"
        disabled={busy !== null}
        onClick={() => void confirm(row, null)}
      >
        Mark no match
      </button>
      <SearchAttach row={row} disabled={busy !== null} onAttach={(dog) => void confirm(row, dog.dogId)} />
    </>
  )

  return (
    <>
      <Card title="Meal-link decisions">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '16px' }}>
          <Stat label="Proposals" value={data.pending.length} />
          <Stat label="Unmatched" value={data.unmatched.length} />
          <Stat label="Exact matches" value={data.exact.length} />
          <Stat label="Awaiting decision" value={total} />
        </div>
      </Card>

      {error === null ? null : <p role="alert" className="card" style={{ padding: '12px 16px' }}>{error}</p>}
      {note === null ? null : <p role="status" className="card" style={{ padding: '12px 16px' }}>{note}</p>}
      {total === 0 ? (
        <Card title="Queue clear">
          <p className="text-muted" style={{ margin: 0 }}>Every meal row has a human decision.</p>
        </Card>
      ) : null}

      <Card title={`Needs a decision (${data.pending.length})`}>
        {data.pending.length === 0 ? (
          <p className="text-muted" style={{ margin: 0 }}>No pending proposals.</p>
        ) : (
          <div style={{ display: 'grid', gap: '14px' }}>
            {data.pending.map((row) => (
              <article key={row.mealSubmissionId} className="card" style={{ padding: '14px' }}>
                <MealRowHeader row={row} tag="Needs decision" />
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '10px' }}>
                  {row.candidates.map((candidate) => candidateButton(row, candidate))}
                  {rowActions(row)}
                </div>
              </article>
            ))}
          </div>
        )}
      </Card>

      <Card title={`No proposed match (${data.unmatched.length})`}>
        {data.unmatched.length === 0 ? (
          <p className="text-muted" style={{ margin: 0 }}>No unmatched rows.</p>
        ) : (
          <div style={{ display: 'grid', gap: '14px' }}>
            {data.unmatched.map((row) => (
              <article key={row.mealSubmissionId} className="card" style={{ padding: '14px' }}>
                <MealRowHeader row={row} tag="Needs decision" />
                <div style={{ marginTop: '10px' }}>{rowActions(row)}</div>
              </article>
            ))}
          </div>
        )}
      </Card>

      <Card title={`Exact matches (${data.exact.length})`}>
        <button
          type="button"
          className="btn btn-primary"
          disabled={busy !== null || data.exact.length === 0}
          onClick={() => void confirmAllExact()}
          style={{ marginBottom: '12px' }}
        >
          Confirm all exact matches
        </button>
        {data.exact.length === 0 ? (
          <p className="text-muted" style={{ margin: 0 }}>No exact matches await confirmation.</p>
        ) : (
          <div style={{ display: 'grid', gap: '14px' }}>
            {data.exact.map((row: AdminExactMealLink) => (
              <article key={row.mealSubmissionId} className="card" style={{ padding: '14px' }}>
                <MealRowHeader row={row} tag="Exact match" />
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '10px' }}>
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={busy !== null}
                    onClick={() => void confirm(row, row.dogId)}
                  >
                    Confirm {mealRowName(row)} — {householdLabel(row.ownerName, row.ownerEmail, 'Household unknown')}
                  </button>
                  {rowActions(row)}
                </div>
              </article>
            ))}
          </div>
        )}
      </Card>

      <Card title={`Confirmed meals (${confirmed.length})`}>
        {confirmed.length === 0 ? (
          <p className="text-muted" style={{ margin: 0 }}>No confirmed meals are editable.</p>
        ) : (
          <div style={{ display: 'grid', gap: '14px' }}>
            {confirmed.map((row) => (
              <article key={row.mealSubmissionId} className="card" style={{ padding: '14px' }}>
                <div style={{ display: 'flex', alignItems: 'start', gap: '10px', flexWrap: 'wrap' }}>
                  <div style={{ flex: '1 1 220px' }}>
                    <MealRowHeader row={row} tag="Confirmed" />
                    <div className="text-muted" style={{ fontSize: '12px', marginTop: '5px' }}>
                      {householdLabel(row.ownerName, row.ownerEmail, 'Household unknown')}
                    </div>
                  </div>
                  {row.ownerEdited ? (
                    <span className="tag tag-accent">Owner edited {fmtDay(parseDay(row.ownerEdited.on))}</span>
                  ) : null}
                  {row.pendingEdit ? <span className="tag tag-accent">Edit queued</span> : null}
                  <button
                    type="button"
                    className="btn btn-ghost"
                    onClick={() => setEditing((current) => current === row.mealSubmissionId ? null : row.mealSubmissionId)}
                  >
                    Edit
                  </button>
                </div>
                {editing === row.mealSubmissionId ? (
                  <MealEditPanel
                    row={row}
                    onSaved={async () => {
                      setNote(`${mealRowName(row)} meal edit queued.`)
                      setEditing(null)
                      await load()
                    }}
                  />
                ) : null}
              </article>
            ))}
          </div>
        )}
      </Card>
    </>
  )
}

function Access(): ReactElement {
  const [allowlist, setAllowlist] = useState<readonly AdminAllowlistEntry[]>([])
  const [staff, setStaff] = useState<readonly AdminStaffMember[]>([])
  const [email, setEmail] = useState('')
  const [note, setNote] = useState<string | null>(null)

  const load = useCallback(() => {
    void adminAllowlist().then((r) => r.ok && setAllowlist(r.data.entries))
    void adminStaff().then((r) => r.ok && setStaff(r.data.staff))
  }, [])
  useEffect(load, [load])

  const act = (path: 'allowlist' | 'staff', body: Record<string, string>): void => {
    setNote(null)
    void adminWrite(path, body).then((result) => {
      setNote(result.ok ? 'Done.' : result.message)
      load()
    })
  }

  return (
    <>
      <Card title="Pilot allowlist">
        <p className="text-muted" style={{ margin: '0 0 12px', fontSize: '12.5px', lineHeight: 1.6 }}>
          While pilot mode is on, only these households can sign in. Everyone else gets the same
          generic reply, so nobody can tell whether an address is on file.
        </p>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}>
          <input
            className="input"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="customer@example.co.uk"
            style={{ minHeight: '42px', flex: '1 1 240px' }}
            aria-label="Customer email address"
          />
          <button type="button" className="btn btn-primary" style={{ minHeight: '42px' }}
                  onClick={() => act('allowlist', { email, action: 'add' })}>
            Admit
          </button>
        </div>
        {allowlist.length === 0 ? (
          <p className="text-muted" style={{ fontSize: '13.5px' }}>Nobody is admitted yet.</p>
        ) : (
          <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'grid', gap: '8px' }}>
            {allowlist.map((entry) => (
              <li key={entry.customerId} style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap', fontSize: '13.5px' }}>
                <span style={{ flex: '1 1 240px' }}>
                  <strong>{entry.dogNames.join(', ') || '(no dogs)'}</strong>
                  <span className="text-muted"> · {entry.emails.join(', ') || 'no email'}</span>
                </span>
                <button type="button" className="btn btn-ghost" style={{ minHeight: '38px', fontSize: '12.5px' }}
                        onClick={() => act('allowlist', { email: entry.emails[0] ?? '', action: 'remove' })}>
                  Remove
                </button>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card title="Staff accounts">
        <p className="text-muted" style={{ margin: '0 0 12px', fontSize: '12.5px', lineHeight: 1.6 }}>
          Ordinary staff sign in with a magic link. A Super Administrator account can only be
          created from the server — a form that mints unrestricted access would be protected by
          exactly the credential it hands out.
        </p>
        <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'grid', gap: '8px' }}>
          {staff.map((member) => (
            <li key={member.email} style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap', fontSize: '13.5px' }}>
              <span style={{ flex: '1 1 260px' }}>
                <strong>{member.email}</strong>
                <span className="text-muted">
                  {' '}· {member.active ? member.role : 'deactivated'} · {member.liveSessions} live
                  {member.lockedUntil === null ? '' : ' · LOCKED'}
                </span>
              </span>
              {member.lockedUntil === null ? null : (
                <button type="button" className="btn btn-ghost" style={{ minHeight: '38px', fontSize: '12.5px' }}
                        onClick={() => act('staff', { email: member.email, action: 'unlock' })}>
                  Unlock
                </button>
              )}
              {member.active ? (
                <button type="button" className="btn btn-ghost" style={{ minHeight: '38px', fontSize: '12.5px' }}
                        onClick={() => act('staff', { email: member.email, action: 'deactivate' })}>
                  Deactivate
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      </Card>

      {note === null ? null : (
        <p role="status" className="card" style={{ padding: '12px 16px', fontSize: '13.5px' }}>
          {note}
        </p>
      )}
    </>
  )
}

function Audit(): ReactElement {
  const [entries, setEntries] = useState<readonly AdminAuditEntry[]>([])

  useEffect(() => {
    void adminAudit(200).then((r) => r.ok && setEntries(r.data.entries))
  }, [])

  return (
    <Card title="Audit trail">
      <p className="text-muted" style={{ margin: '0 0 12px', fontSize: '12.5px', lineHeight: 1.6 }}>
        Every record served, every document opened, every emergency contact revealed, every
        administrative change. This is what makes “a wrong record was shown” a question with an
        answer.
      </p>
      <div style={{ overflowX: 'auto' }}>
        <table className="table" style={{ fontSize: '12.5px', minWidth: '640px' }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left' }}>When</th>
              <th style={{ textAlign: 'left' }}>Who</th>
              <th style={{ textAlign: 'left' }}>Did what</th>
              <th style={{ textAlign: 'left' }}>To</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((entry, index) => (
              <tr key={`${entry.at}-${index}`}>
                <td style={{ whiteSpace: 'nowrap' }}>{new Date(entry.at).toLocaleString('en-GB')}</td>
                <td style={{ overflowWrap: 'anywhere' }}>{entry.actor}</td>
                <td>{entry.action}</td>
                <td style={{ overflowWrap: 'anywhere' }}>{entry.target ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  )
}
